/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from 'react';

import { isEmpty } from 'ramda';
import Carousel from 'react-multi-carousel';

import {
  ContentReviewsWrapper,
  ContentReviewsOverview,
  ContentReadMoreOverview,
  ContentReviewsSections,
  ContentReadMoreYellowBar,
  CarouselWrapper,
  SeeAllReviewsLink
} from './styles';

import ContentReview from './ContentReview';
import OverallReviewArea from './OverallReviewArea';
import ContentReviewsHeader from './ContentReviewsHeader';
import ContentReviewsStructuredData from './ContentReviewsStructuredData';

const responsive = {
  wideDesktop: {
    breakpoint: {
      max: 9000,
      min: 1200
    },
    items: 3,
    slidesToSlide: 3
  },
  desktop: {
    breakpoint: {
      max: 3000,
      min: 1200
    },
    items: 3,
    slidesToSlide: 3
  },
  tablet: {
    breakpoint: {
      max: 1200,
      min: 800
    },
    items: 2,
    slidesToSlide: 2
  },
  mobile: {
    breakpoint: {
      max: 800,
      min: 0
    },
    items: 1,
    slidesToSlide: 1
  }
};

type TFlags = {
  isMultiMake: boolean;
  isNoMake: boolean;
  isSingleModel: boolean;
  isAllModels: boolean;
  hasCvnaid: boolean;
};
type TVehicle = {
  make: string;
  model: string;
};
type TReviewObj = {
  additional: object;
  customer: object;
  nps_score: string;
  review_date: string;
  review_published_last_date: string;
  review_id: string;
  review_text: string;
  review_title: string;
  review_score: string;
  vehicle: TVehicle;
};

type TSeoData = {
  seoH1: string;
  flags: TFlags;
};
interface IContentReviews {
  showStructuredData?: boolean;
  highestRating: string;
  lowestRating: string;
  overallCount: number;
  overallAverage: number;
  seoData: TSeoData;
  trackEvent?: any;
  hasCvnaid: boolean;
  contentReviews: TReviewObj[];
}

export const ContentReviews = ({
  contentReviews,
  overallAverage,
  overallCount,
  hasCvnaid,
  seoData,
  trackEvent,
  highestRating,
  lowestRating,
  showStructuredData
}: IContentReviews) => {
  const reviews = contentReviews || [];
  const { flags } = seoData;
  const [{ vehicle }] = reviews;
  const { make: reviewMake, model: reviewModel } = vehicle;
  const hasReviews = !isEmpty(reviews);
  const [prevSlide, setPrevSlide] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(1);
  const isInitialRender = useRef(true);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;

      return;
    }

    const slideDirection = prevSlide < currentSlide ? 'Next' : 'Previous';
    trackEvent(`SEOReviews - ${slideDirection} Arrow Click`, {
      eventCategory: 'SEOReviews',
      eventAction: 'click',
      eventLabel: `Reviews Carousel ${slideDirection} Arrow`
    });
  }, [currentSlide]);

  return !hasReviews ? null : (
    <ContentReviewsWrapper data-cv-test="Cv.Search.ContentReviews.Wrapper">
      <ContentReviewsOverview>
        {showStructuredData ? (
          <ContentReviewsStructuredData
            hasReviews={hasReviews}
            flags={flags}
            reviewMake={reviewMake}
            reviewModel={reviewModel}
            overallAverage={overallAverage.toString()}
            overallCount={overallCount.toString()}
            highestRating={highestRating}
            lowestRating={lowestRating}
          />
        ) : (
          <> </>
        )}

        <ContentReviewsSections>
          <ContentReadMoreOverview>
            <ContentReadMoreYellowBar />
            <ContentReviewsHeader
              shouldRender={hasCvnaid}
              reviewsTitle={seoData.seoH1}
            />
          </ContentReadMoreOverview>
          <OverallReviewArea score={overallAverage} count={overallCount} />
          <CarouselWrapper>
            <Carousel
              afterChange={(previousSlide, state) => {
                setPrevSlide(previousSlide);
                setCurrentSlide(state.currentSlide);
              }}
              additionalTransfrom={0}
              arrows
              autoPlaySpeed={3000}
              centerMode={false}
              containerClass="reviews-carousel-container"
              draggable
              focusOnSelect={false}
              infinite={false}
              itemClass="reviews-carousel-item"
              keyBoardControl
              minimumTouchDrag={80}
              partialVisible
              renderButtonGroupOutside={false}
              renderDotsOutside={false}
              responsive={responsive}
              showDots
              sliderClass=""
              slidesToSlide={3}
              ssr
              swipeable
              deviceType="mobile"
            >
              {contentReviews.map(review => (
                <div key={review.review_title}>
                  <ContentReview item={review} useFade />
                </div>
              ))}
            </Carousel>
            <SeeAllReviewsLink href="/reviews">
              See all customers reviews
            </SeeAllReviewsLink>
          </CarouselWrapper>
        </ContentReviewsSections>
      </ContentReviewsOverview>
    </ContentReviewsWrapper>
  );
};

export default ContentReviews;
